import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import { TrendingUp, TrendingDown, Minus, RotateCcw, Loader2, History } from "lucide-react";

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDateFull(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1c] border border-zinc-700 rounded-lg px-4 py-3 shadow-xl">
      <p className="text-xs text-zinc-500 mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-semibold" style={{ color: p.color }}>
          {p.name}: {p.value}%
        </p>
      ))}
    </div>
  );
}

export default function ScoreHistory({ history, onRescan, rescanLoading }) {
  if (!history || history.total_scans < 2) return null;

  const data = history.history.map((h, i) => ({
    scan: `Scan ${i + 1}`,
    date: formatDate(h.created_at),
    dateFull: formatDateFull(h.created_at),
    Overall: h.overall_score,
    SEO: h.seo_score,
    AI: h.ai_score,
  }));

  const latest = history.history[history.history.length - 1];
  const previous = history.history[history.history.length - 2];
  const overallDiff = latest.overall_score - previous.overall_score;
  const seoDiff = latest.seo_score - previous.seo_score;
  const aiDiff = latest.ai_score - previous.ai_score;

  const DiffBadge = ({ diff, label }) => (
    <div className="flex items-center gap-2">
      <span className="text-xs text-zinc-500">{label}</span>
      <span className={`flex items-center gap-1 text-xs font-bold ${diff > 0 ? "text-green-400" : diff < 0 ? "text-red-400" : "text-zinc-500"}`}>
        {diff > 0 ? <TrendingUp className="w-3 h-3" /> : diff < 0 ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
        {diff > 0 ? "+" : ""}{diff}%
      </span>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="border border-zinc-800 rounded-xl overflow-hidden mb-10"
      data-testid="score-history"
    >
      {/* Header */}
      <div className="px-8 py-5 border-b border-zinc-800 bg-[#1a1a1c]/40 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <History className="w-5 h-5 text-orange-500" />
          <div>
            <h3 className="text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Score History</h3>
            <p className="text-xs text-zinc-500">{history.total_scans} scans tracked</p>
          </div>
        </div>
        <button
          onClick={onRescan}
          disabled={rescanLoading}
          className="flex items-center gap-2 text-sm font-semibold text-orange-500 hover:text-orange-400 border border-orange-500/30 px-4 py-2 rounded-md hover:bg-orange-500/10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="rescan-button"
        >
          {rescanLoading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Scanning...</>
          ) : (
            <><RotateCcw className="w-4 h-4" /> Re-scan Now</>
          )}
        </button>
      </div>

      {/* Change indicators */}
      <div className="px-8 py-4 border-b border-zinc-800/50 flex items-center gap-6 bg-[#1a1a1c]/20">
        <span className="text-xs text-zinc-600 uppercase tracking-wider font-semibold">Since last scan:</span>
        <DiffBadge diff={overallDiff} label="Overall" />
        <DiffBadge diff={seoDiff} label="SEO" />
        <DiffBadge diff={aiDiff} label="AI" />
      </div>

      {/* Chart */}
      <div className="px-6 py-6">
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
            <defs>
              <linearGradient id="gradOverall" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f97316" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradSEO" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradAI" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#a855f7" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 12 }} axisLine={{ stroke: "#27272a" }} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fill: "#71717a", fontSize: 12 }} axisLine={{ stroke: "#27272a" }} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="Overall" stroke="#f97316" strokeWidth={2.5} fill="url(#gradOverall)" dot={{ fill: "#f97316", r: 4, strokeWidth: 0 }} activeDot={{ r: 6, fill: "#f97316", stroke: "#fff", strokeWidth: 2 }} />
            <Area type="monotone" dataKey="SEO" stroke="#3b82f6" strokeWidth={1.5} fill="url(#gradSEO)" dot={{ fill: "#3b82f6", r: 3, strokeWidth: 0 }} />
            <Area type="monotone" dataKey="AI" stroke="#a855f7" strokeWidth={1.5} fill="url(#gradAI)" dot={{ fill: "#a855f7", r: 3, strokeWidth: 0 }} />
          </AreaChart>
        </ResponsiveContainer>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-2">
          {[
            { color: "#f97316", label: "Overall" },
            { color: "#3b82f6", label: "SEO" },
            { color: "#a855f7", label: "AI Visibility" },
          ].map((l, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className="w-3 h-1 rounded-full" style={{ background: l.color }} />
              <span className="text-xs text-zinc-500">{l.label}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
